import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { TitlebarContextProps } from './TitlebarContext.types'

const TitlebarContext = createContext<TitlebarContextProps | undefined>(undefined)

export const TitlebarContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null)
  const [menusVisible, setMenusVisible] = useState(false)
  const closeActiveMenu = useCallback(() => setActiveMenuIndex(null), [])

  // Sidebar toggle callbacks (registered by MainLayout, called by Titlebar)
  const sidebarToggleRef = useRef<(() => void) | null>(null)
  const rightSidebarToggleRef = useRef<(() => void) | null>(null)

  // Sidebar open state (set by MainLayout, read by Titlebar for icon switching)
  const [sidebarLeftOpen, setSidebarLeftOpen] = useState(true)
  const [sidebarRightOpen, setSidebarRightOpen] = useState(true)

  // Tracks whether MainLayout is mounted (used by Titlebar to show app controls on web)
  const [mainLayoutActive, setMainLayoutActive] = useState(false)

  // TabBar portal slot: Titlebar provides a DOM element, MainLayout portals TabBar into it
  const tabBarSlotRef = useRef<HTMLDivElement | null>(null)
  const [tabBarSlotReady, setTabBarSlotReady] = useState(false)

  // Stable callback ref for Titlebar to register the slot element
  const registerTabBarSlot = useCallback((el: HTMLDivElement | null) => {
    tabBarSlotRef.current = el
    setTabBarSlotReady(!!el)
  }, [])

  // Union shape portal slot: WindowContext provides a DOM element, MainLayout portals ContentUnionShape into it
  const unionShapeSlotRef = useRef<HTMLDivElement | null>(null)
  const [unionShapeSlotReady, setUnionShapeSlotReady] = useState(false)

  const registerUnionShapeSlot = useCallback((el: HTMLDivElement | null) => {
    unionShapeSlotRef.current = el
    setUnionShapeSlotReady(!!el)
  }, [])

  // Bump portal slot: inside Titlebar so bump layers share the same stacking context as tab labels.
  // This prevents Chromium's compositor from reordering bump above labels after content repaints.
  const bumpSlotRef = useRef<HTMLDivElement | null>(null)
  const [bumpSlotReady, setBumpSlotReady] = useState(false)

  const registerBumpSlot = useCallback((el: HTMLDivElement | null) => {
    bumpSlotRef.current = el
    setBumpSlotReady(!!el)
  }, [])

  const contextValue = useMemo<TitlebarContextProps>(() => ({
    activeMenuIndex, setActiveMenuIndex, menusVisible, setMenusVisible, closeActiveMenu,
    sidebarToggleRef,
    rightSidebarToggleRef,
    sidebarLeftOpen,
    setSidebarLeftOpen,
    sidebarRightOpen,
    setSidebarRightOpen,
    mainLayoutActive,
    setMainLayoutActive,
    tabBarSlotRef,
    tabBarSlotReady,
    registerTabBarSlot,
    unionShapeSlotRef,
    unionShapeSlotReady,
    registerUnionShapeSlot,
    bumpSlotRef,
    bumpSlotReady,
    registerBumpSlot,
  }), [activeMenuIndex, menusVisible, sidebarLeftOpen, sidebarRightOpen, mainLayoutActive, tabBarSlotReady, unionShapeSlotReady, bumpSlotReady, closeActiveMenu, registerTabBarSlot, registerUnionShapeSlot, registerBumpSlot])

  return (
    <TitlebarContext.Provider value={contextValue}>
      {children}
    </TitlebarContext.Provider>
  )
}

export const useTitlebarContext = (): TitlebarContextProps => {
  const context = useContext(TitlebarContext)
  if (context === undefined) {
    throw new Error('useTitlebarContext must be used within a TitlebarContext')
  }
  return context
}
